import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Search } from 'lucide-react';
import { api } from '@/utils/api';
import { StockFilters, EMPTY_STOCK_FILTER, stockFilterParams, type StockFilterValue } from './StockFilters';

/**
 * 🔒 2026-09-21 (Manak) — Stock Overview round. Spec: mockups/2026-09-21-stock-overview-LOCKED-SPEC.md
 *  - ATP is shown as "Available Stock" (meaning unchanged).
 *  - Pending Pack & QA = Grade A graded-not-packed + packed-awaiting-QA; Current Stock = Available + Pending.
 *  - Blocked stays, with a cause split and an age tag.
 *  - In Transit = supplier POs only. Units between our own buildings are not a column; the footer
 *    links to Transfers when there are any.
 */
type FullRow = {
  productId: string; name: string; sku: string | null;
  factoryA: number; warehouseA: number; reserved: number; blocked: number; atp: number; gradeB: number;
  pendingGraded: number; pendingPackedQa: number; pendingPackQa: number; currentStock: number;
  inTransit: number; blockedQaFail: number; blockedCountMissing: number; blockedOldestDays: number | null; blockedAgeing: boolean;
};
type AtpRow = { productId: string; name: string; sku: string | null; atp: number };
type ColKey = 'factoryA' | 'warehouseA' | 'reserved' | 'blocked' | 'atp' | 'pendingPackQa' | 'currentStock' | 'gradeB' | 'inTransit';

const FULL_COLS: Array<[ColKey, string]> = [
  ['factoryA', 'Factory A'], ['warehouseA', 'Warehouse A'], ['reserved', 'Reserved'], ['blocked', 'Blocked'],
  ['atp', 'Available Stock'], ['pendingPackQa', 'Pending Pack & QA'], ['currentStock', 'Current Stock'],
  ['gradeB', 'Grade B'], ['inTransit', 'In Transit (suppliers)'],
];
const STRONG = new Set<ColKey>(['atp', 'currentStock']);

function Cell({ row, k }: { row: FullRow; k: ColKey }) {
  const v = row[k];
  const cls = STRONG.has(k) ? 'font-semibold text-gray-900' : k === 'blocked' && v > 0 ? 'text-red-600' : 'text-gray-700';
  return (
    <td className={`px-4 py-3 text-right align-top ${cls}`}>
      <span data-main>{v}</span>
      {k === 'blocked' && v > 0 && row.blockedOldestDays !== null && (
        <span className={`ml-1.5 inline-flex rounded-full px-1.5 text-[11px] font-medium ${row.blockedAgeing ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
          {row.blockedOldestDays} days
        </span>
      )}
      {k === 'blocked' && v > 0 && (
        <span className="block text-[11px] font-normal text-gray-500">QA fail {row.blockedQaFail} · Count missing {row.blockedCountMissing}</span>
      )}
      {k === 'pendingPackQa' && v > 0 && (
        <span className="block text-[11px] font-normal text-gray-500">Graded {row.pendingGraded} · Packed {row.pendingPackedQa}</span>
      )}
    </td>
  );
}

export function LocationGradePivot() {
  const navigate = useNavigate();
  const [view, setView] = useState<'full' | 'atp_only'>('full');
  const [rows, setRows] = useState<Array<FullRow | AtpRow>>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [betweenBuildings, setBetweenBuildings] = useState(0);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<StockFilterValue>(EMPTY_STOCK_FILTER);
  const [loading, setLoading] = useState(true);
  const limit = 20;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get('/atp', { params: { page: String(page), limit: String(limit), ...(search ? { search } : {}), ...stockFilterParams(filters) } })
      .then((res) => {
        if (!alive) return;
        setView(res.data.data.view); setRows(res.data.data.rows); setTotal(res.data.data.pagination.total);
        setBetweenBuildings(Number(res.data.data.internalTransferTotal ?? 0));
      })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [page, search, filters]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="mb-6 rounded-xl border border-gray-200 bg-white overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
        <p className="text-sm font-semibold text-gray-900">By location and grade</p>
        <div className="flex flex-wrap items-center gap-2">
          <StockFilters label="By location and grade" value={filters} onChange={(v) => { setPage(1); setFilters(v); }} />
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }} placeholder="Search product or SKU" className="rounded-lg border border-gray-200 py-1.5 pl-8 pr-3 text-sm" />
          </div>
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 size={18} className="animate-spin text-gray-400" /></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs font-medium text-gray-500">
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">SKU</th>
                {view === 'full' ? FULL_COLS.map(([, label]) => <th key={label} className="px-4 py-3 text-right">{label}</th>) : <th className="px-4 py-3 text-right">Available Stock</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => (
                <tr key={r.productId} className="cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => navigate(`/warehouse/product-units?productId=${encodeURIComponent(r.productId)}`)}>
                  <td className="px-4 py-3 font-medium text-gray-900 align-top">{r.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600 align-top">{r.sku ?? '-'}</td>
                  {view === 'full'
                    ? FULL_COLS.map(([key]) => <Cell key={key} row={r as FullRow} k={key} />)
                    : <td className="px-4 py-3 text-right font-semibold text-gray-900">{r.atp}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-xs text-gray-500 border-t border-gray-100">
        <span>
          Available Stock = Grade A (Factory + Warehouse) − Reserved − Blocked. Current Stock = Available Stock + Pending Pack &amp; QA. In Transit = PO quantity sent, not yet received.
          {view === 'full' && betweenBuildings > 0 && (
            <> · <Link to="/warehouse/transfers" className="font-medium text-blue-700 hover:underline" onClick={(e) => e.stopPropagation()}>{betweenBuildings} unit{betweenBuildings === 1 ? '' : 's'} moving between buildings — see Transfers</Link></>
          )}
        </span>
        {totalPages > 1 && (
          <span className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded border border-gray-200 px-2 py-0.5 disabled:opacity-40">Prev</button>
            {page} / {totalPages}
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded border border-gray-200 px-2 py-0.5 disabled:opacity-40">Next</button>
          </span>
        )}
      </div>
    </div>
  );
}
